/**
 * The module registry of the Control Center, after the owner's service-console reference
 * (docs/design/tino-service-console-70-layouts.html, `MODULES`): the sidebar shows one module at
 * a time, picked in "Không gian quản lý", plus a "Dùng chung" group of shared entries.
 *
 * This is the one place a module is declared. Hosting, mail and cloud are other Tino products
 * that will join this system later; they are listed here disabled (no entries, never shown)
 * so adding one is: give it groups, set `enabled`, add its hubs to `nav/hubs.ts`. The platform
 * module holds the module-neutral areas every product shares (customers, orders,
 * invoices, wallets, operations, settings).
 *
 * Plain data so node tests can load it. Labels: `ux:modules.<id>`, `ux:sidebar.groups.<group>`,
 * `ux:sidebar.entries.<entry>`.
 */

import type { HubId, HubLocation } from './hubs';

export interface SidebarEntry {
  id: string;
  hub: HubId;
  /** The tab it opens; none: the hub's first tab. */
  tab?: string;
  /** Other tabs of the hub that light this entry. */
  also?: readonly string[];
}

export interface SidebarGroup {
  id: string;
  entries: readonly SidebarEntry[];
}

export type ModuleId = 'platform' | 'domains' | 'hosting' | 'email' | 'cloud' | 'backorder' | 'auctions';

export interface ModuleDef {
  id: ModuleId;
  /** Shown in the picker. A disabled module is reserved for a product not built yet. */
  enabled: boolean;
  groups: readonly SidebarGroup[];
}

const E = {
  home: { id: 'home', hub: 'home', tab: 'queue', also: ['protected', 'approvals', 'deadlines', 'requests'] },
  customers: { id: 'customers', hub: 'customers', tab: 'customers' },
  orders: { id: 'orders', hub: 'sales', tab: 'orders', also: ['quotes', 'overview'] },
  invoices: { id: 'invoices', hub: 'sales', tab: 'invoices', also: ['payments'] },
  ledger: { id: 'ledger', hub: 'sales', tab: 'ledger' },
  ops: { id: 'ops', hub: 'ops', tab: 'overview', also: ['queues', 'alerts', 'services', 'telemetry', 'slo'] },
  settings: { id: 'settings', hub: 'settings' },
} as const satisfies Record<string, SidebarEntry>;

export const MODULES: readonly ModuleDef[] = [
  {
    // Module-neutral: shared by every product.
    id: 'platform',
    enabled: true,
    groups: [
      { id: 'work', entries: [E.home, E.customers] },
      { id: 'commerce', entries: [E.orders, E.invoices, E.ledger] },
      { id: 'infra', entries: [E.ops, E.settings] },
    ],
  },
  {
    id: 'domains',
    enabled: true,
    groups: [
      {
        id: 'domains',
        entries: [
          { id: 'domains', hub: 'domains' },
          { id: 'dossiers', hub: 'home', tab: 'dossiers' },
          { id: 'subjects', hub: 'customers', tab: 'subjects', also: ['documents', 'dossiers'] },
        ],
      },
      {
        id: 'catalog',
        entries: [
          { id: 'products', hub: 'products' },
          { id: 'addons', hub: 'products', tab: 'services' },
          { id: 'quote', hub: 'products', tab: 'quote' },
        ],
      },
      {
        id: 'registry',
        entries: [
          { id: 'registry', hub: 'ops', tab: 'registry' },
          { id: 'compliance', hub: 'compliance' },
        ],
      },
    ],
  },
  // Other Tino products, joining later (reference screens 29–57). No entries until they exist.
  { id: 'hosting', enabled: false, groups: [] },
  { id: 'email', enabled: false, groups: [] },
  { id: 'cloud', enabled: false, groups: [] },
  {
    id: 'backorder',
    enabled: true,
    groups: [
      {
        id: 'backorder',
        entries: [
          { id: 'backorder', hub: 'backorder', tab: 'overview' },
          { id: 'drops', hub: 'backorder', tab: 'drops' },
          { id: 'backorders', hub: 'backorder', tab: 'requests' },
          { id: 'catching', hub: 'backorder', tab: 'catching' },
          { id: 'holds', hub: 'backorder', tab: 'holds' },
        ],
      },
    ],
  },
  {
    id: 'auctions',
    enabled: true,
    groups: [
      {
        id: 'auctions',
        entries: [
          { id: 'auctions', hub: 'auctions', tab: 'market' },
          { id: 'auctionSessions', hub: 'auctions', tab: 'sessions' },
          { id: 'bids', hub: 'auctions', tab: 'bids' },
          { id: 'settlement', hub: 'auctions', tab: 'settlement' },
          { id: 'handover', hub: 'auctions', tab: 'handover' },
        ],
      },
    ],
  },
];

/** "Dùng chung": platform entries also listed under every product module. */
export const SHARED_GROUP: SidebarGroup = { id: 'shared', entries: [E.home, E.customers, E.orders] };

export const ENABLED_MODULES: readonly ModuleDef[] = MODULES.filter((m) => m.enabled);

export function findModule(id: string): ModuleDef | undefined {
  return MODULES.find((m) => m.id === id);
}

/** Every entry of every enabled module, once (a module's own groups; the shared group repeats platform entries). */
export const ALL_ENTRIES: readonly SidebarEntry[] = ENABLED_MODULES.flatMap((m) => m.groups.flatMap((g) => g.entries));

/** The module whose own groups hold an entry. */
export function ownerModule(entryId: string): ModuleDef | undefined {
  return ENABLED_MODULES.find((m) => m.groups.some((g) => g.entries.some((e) => e.id === entryId)));
}

/** The groups shown for a module: its own, then "Dùng chung" (not repeated under the platform). */
export function moduleGroups(m: ModuleDef): readonly SidebarGroup[] {
  return m.id === 'platform' ? m.groups : [...m.groups, SHARED_GROUP];
}

/**
 * The module to show for the entry of the current page. A shared entry keeps the module the
 * user was in (orders opened from the backorder module stay in it); anything else shows its owner.
 */
export function moduleFor(entryId: string | undefined, previous: ModuleId | undefined): ModuleId {
  const prev = previous ? ENABLED_MODULES.find((m) => m.id === previous) : undefined;
  if (!entryId) return prev?.id ?? 'platform';
  if (prev && prev.id !== 'platform' && SHARED_GROUP.entries.some((e) => e.id === entryId)) return prev.id;
  return ownerModule(entryId)?.id ?? prev?.id ?? 'platform';
}

/** The first entry of a module: where picking it in "Không gian quản lý" lands. */
export function moduleHome(m: ModuleDef): SidebarEntry | undefined {
  return m.groups[0]?.entries[0];
}

/** The entry to light for a location: the one naming its tab, else the one for the whole hub. */
export function sidebarEntryFor(here: HubLocation | null): SidebarEntry | undefined {
  if (!here) return undefined;
  const all = ALL_ENTRIES.filter((e) => e.hub === here.hub);
  return all.find((e) => e.tab === here.tab || e.also?.includes(here.tab)) ?? all.find((e) => !e.tab) ?? all[0];
}
